'use client';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { ArrowLeft, Send, Mic, Camera, Phone, Play, Pause, Check, CheckCheck, Image as ImageIcon, PhoneCall, PhoneMissed, PhoneIncoming, PhoneOutgoing, X, Clock, Eye, Headphones, AlertCircle,  } from 'lucide-react';
import AppImage from '@/components/ui/AppImage';
import AppLayout from '@/components/AppLayout';
import { ANDRE_PHOTO_URL, formatDurationLabel, type Message } from '@/lib/familyDemoData';
import { useFamilyDemo } from '@/lib/familyDemoState';
import Icon from '@/components/ui/AppIcon';


// ─── Helpers ────────────────────────────────────────────────────────────────

function fmtTime(iso: string): string {
  try {
    const d = new Date(iso);
    return `${d.getHours().toString().padStart(2, '0')}h${d.getMinutes().toString().padStart(2, '0')}`;
  } catch { return ''; }
}

function fmtDateLabel(iso: string): string {
  try {
    const d = new Date(iso);
    const now = new Date();
    if (d.toDateString() === now.toDateString()) return "Aujourd'hui";
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    if (d.toDateString() === yesterday.toDateString()) return 'Hier';
    return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
  } catch { return ''; }
}

// ─── Status icon ────────────────────────────────────────────────────────────

function StatusIcon({ status }: { status: Message['readStatus'] }) {
  if (status === 'sent') return <Check size={12} className="text-white/60" aria-label="Envoyé" />;
  if (status === 'received') return <CheckCheck size={12} className="text-white/60" aria-label="Reçu" />;
  if (status === 'seen') return <CheckCheck size={12} className="text-white" aria-label="Vu" />;
  if (status === 'listened') return <Headphones size={12} className="text-white" aria-label="Écouté" />;
  if (status === 'handled') return <Check size={12} className="text-lucat-green" aria-label="Traité" />;
  return null;
}

// ─── Voice player ────────────────────────────────────────────────────────────

function VoicePlayer({ msg, isOut }: { msg: Message; isOut: boolean }) {
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const duration = msg.durationSeconds ?? 30;

  const toggle = () => {
    if (playing) {
      clearInterval(intervalRef.current!);
      setPlaying(false);
    } else {
      setPlaying(true);
      const step = 100 / (duration * 10);
      intervalRef.current = setInterval(() => {
        setProgress((p) => {
          if (p >= 100) {
            clearInterval(intervalRef.current!);
            setPlaying(false);
            return 0;
          }
          return p + step;
        });
      }, 100);
    }
  };

  useEffect(() => () => { if (intervalRef.current) clearInterval(intervalRef.current); }, []);

  const elapsed = Math.round((progress / 100) * duration);
  const remaining = duration - elapsed;
  const fmtSec = (s: number) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;

  return (
    <div className={`flex items-center gap-2.5 px-3 py-2.5 rounded-2xl shadow-sm min-w-[200px] ${isOut ? 'bg-lucat-green text-white rounded-br-sm' : 'bg-white text-foreground rounded-bl-sm'}`}>
      <button
        onClick={toggle}
        aria-label={playing ? 'Pause' : 'Lire le message vocal'}
        className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${isOut ? 'bg-white/20 hover:bg-white/30' : 'bg-lucat-purple/10 hover:bg-lucat-purple/20'}`}
      >
        {playing
          ? <Pause size={16} className={isOut ? 'text-white' : 'text-lucat-purple'} />
          : <Play size={16} className={isOut ? 'text-white' : 'text-lucat-purple'} />
        }
      </button>
      <div className="flex-1 flex flex-col gap-1">
        <div className="relative h-1.5 rounded-full overflow-hidden bg-white/30">
          <div
            className={`absolute left-0 top-0 h-full rounded-full transition-all ${isOut ? 'bg-white' : 'bg-lucat-purple'}`}
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="flex justify-between">
          <span className={`text-[10px] ${isOut ? 'text-white/70' : 'text-muted-foreground'}`}>{fmtSec(elapsed)}</span>
          <span className={`text-[10px] ${isOut ? 'text-white/70' : 'text-muted-foreground'}`}>{fmtSec(remaining)}</span>
        </div>
      </div>
    </div>
  );
}

// ─── Photo bubble ────────────────────────────────────────────────────────────

function PhotoBubble({ msg, isOut, onOpen }: { msg: Message; isOut: boolean; onOpen: (url: string) => void }) {
  const url = msg.mediaUrl ?? msg.thumbnailUrl ?? '';
  return (
    <div className={`rounded-2xl overflow-hidden shadow-sm max-w-[220px] ${isOut ? 'rounded-br-sm' : 'rounded-bl-sm'}`}>
      {url ? (
        <button
          onClick={() => onOpen(url)}
          aria-label="Voir la photo en plein écran"
          className="block w-full"
        >
          <AppImage
            src={url}
            alt="Photo partagée avec André"
            width={220}
            height={160}
            className="w-full h-40 object-cover"
          />
        </button>
      ) : (
        <div className="w-full h-40 bg-muted flex items-center justify-center">
          <p className="text-muted-foreground text-xs text-center px-3">Ce contenu n&apos;est pas disponible pour le moment</p>
        </div>
      )}
      {msg.content && (
        <div className={`px-3 py-2 text-sm ${isOut ? 'bg-lucat-green text-white' : 'bg-white text-foreground'}`}>
          {msg.content}
        </div>
      )}
      {url && (
        <button
          onClick={() => onOpen(url)}
          aria-label="Voir la photo"
          className={`w-full py-1.5 text-xs font-semibold flex items-center justify-center gap-1 ${isOut ? 'bg-lucat-green/80 text-white' : 'bg-lucat-teal-light text-lucat-teal'}`}
        >
          <Eye size={12} />
          Voir la photo
        </button>
      )}
    </div>
  );
}

// ─── Call bubble ─────────────────────────────────────────────────────────────

function CallBubble({ msg, isOut, onCallAgain }: { msg: Message; isOut: boolean; onCallAgain: () => void }) {
  const isMissed = msg.callStatus === 'missed';
  const isIncoming = msg.direction === 'incoming';

  const Icon = isMissed
    ? PhoneMissed
    : isIncoming
    ? PhoneIncoming
    : PhoneOutgoing;

  const label = isMissed
    ? "Appel manqué d'André"
    : isIncoming
    ? `Appel ${msg.callType === 'video' ? 'vidéo' : 'audio'} d'André`
    : `Appel ${msg.callType === 'video' ? 'vidéo' : 'audio'} — ${msg.durationSeconds ? formatDurationLabel(msg.durationSeconds) : ''}`;

  return (
    <div className={`flex items-center gap-2.5 px-3 py-2.5 rounded-2xl shadow-sm ${isMissed ? 'bg-lucat-orange-light border border-lucat-orange/30' : isOut ? 'bg-lucat-green/10 border border-lucat-green/30' : 'bg-white border border-border'} ${isOut ? 'rounded-br-sm' : 'rounded-bl-sm'}`}>
      <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${isMissed ? 'bg-lucat-orange/20' : 'bg-lucat-green/20'}`}>
        <Icon size={16} className={isMissed ? 'text-lucat-orange' : 'text-lucat-green'} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-foreground leading-tight">{label}</p>
        {msg.durationSeconds && !isMissed && (
          <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
            <Clock size={10} />
            {formatDurationLabel(msg.durationSeconds)}
          </p>
        )}
      </div>
      <button
        onClick={onCallAgain}
        aria-label="Appeler à nouveau"
        className="flex-shrink-0 bg-lucat-green text-white rounded-xl px-2.5 py-1.5 text-xs font-bold flex items-center gap-1 tap-target active:scale-95 transition-all"
      >
        <Phone size={12} />
        Rappeler
      </button>
    </div>
  );
}

// ─── Callback request card ────────────────────────────────────────────────────

function CallbackCard({ msg, onCall, onLater }: { msg: Message; onCall: () => void; onLater: () => void }) {
  const [handled, setHandled] = useState(msg.readStatus === 'handled');

  if (handled) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-2xl bg-muted/60 border border-border rounded-bl-sm max-w-[280px]">
        <Check size={14} className="text-lucat-green flex-shrink-0" />
        <p className="text-xs text-muted-foreground">Demande de rappel traitée</p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl overflow-hidden shadow-sm border border-lucat-orange/40 bg-lucat-orange-light rounded-bl-sm max-w-[280px]">
      <div className="px-3 pt-3 pb-2 flex items-start gap-2">
        <div className="w-8 h-8 rounded-full bg-lucat-orange/20 flex items-center justify-center flex-shrink-0">
          <PhoneCall size={15} className="text-lucat-orange" />
        </div>
        <div>
          <p className="text-sm font-bold text-lucat-navy leading-tight">André souhaite être appelé</p>
          <p className="text-xs text-muted-foreground mt-0.5">Appuyez pour le rappeler maintenant</p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-1.5 px-3 pb-3">
        <button
          onClick={() => { setHandled(true); onCall(); }}
          aria-label="Rappeler André"
          className="bg-lucat-green text-white rounded-xl py-2 text-xs font-bold flex items-center justify-center gap-1 tap-target active:scale-95 transition-all"
        >
          <Phone size={12} />
          Rappeler André
        </button>
        <button
          onClick={() => { setHandled(true); onLater(); }}
          aria-label="Plus tard"
          className="bg-white text-lucat-orange rounded-xl py-2 text-xs font-semibold border border-lucat-orange/30 tap-target active:scale-95 transition-all"
        >
          Plus tard
        </button>
      </div>
    </div>
  );
}

// ─── Notification bubble ──────────────────────────────────────────────────────

function NotifBubble({ msg }: { msg: Message }) {
  return (
    <div className="flex justify-center my-1">
      <span className="bg-white/80 text-muted-foreground text-[11px] font-medium px-3 py-1 rounded-full shadow-sm border border-border/50 flex items-center gap-1.5">
        <AlertCircle size={10} className="text-lucat-teal" />
        {msg.content}
      </span>
    </div>
  );
}

// ─── Message bubble ───────────────────────────────────────────────────────────

function MessageBubble({
  msg,
  onOpenPhoto,
  onCallAgain,
  onCallbackCall,
  onCallbackLater,
}: {
  msg: Message;
  onOpenPhoto: (url: string) => void;
  onCallAgain: () => void;
  onCallbackCall: (notifId?: string) => void;
  onCallbackLater: (notifId?: string) => void;
}) {
  const isOut = msg.direction === 'outgoing';

  if (msg.type === 'notification') {
    if (msg.notifId) {
      // Callback request card — always incoming/left
      return (
        <div className="flex justify-start mb-3">
          <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0 mr-2 self-end">
            <AppImage src={ANDRE_PHOTO_URL} alt="André" width={32} height={32} className="w-full h-full object-cover object-top" />
          </div>
          <div className="flex flex-col gap-0.5 max-w-[80%]">
            <CallbackCard
              msg={msg}
              onCall={() => onCallbackCall(msg.notifId)}
              onLater={() => onCallbackLater(msg.notifId)}
            />
            <div className="flex items-center gap-1 px-1">
              <span className="text-[10px] text-muted-foreground">{fmtTime(msg.sentAt)}</span>
            </div>
          </div>
        </div>
      );
    }
    return <NotifBubble msg={msg} />;
  }

  if (msg.type === 'call') {
    return (
      <div className={`flex ${isOut ? 'justify-end' : 'justify-start'} mb-3`}>
        {!isOut && (
          <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0 mr-2 self-end">
            <AppImage src={ANDRE_PHOTO_URL} alt="André" width={32} height={32} className="w-full h-full object-cover object-top" />
          </div>
        )}
        <div className={`flex flex-col gap-0.5 max-w-[80%] ${isOut ? 'items-end' : 'items-start'}`}>
          <CallBubble msg={msg} isOut={isOut} onCallAgain={onCallAgain} />
          <div className={`flex items-center gap-1 px-1 ${isOut ? 'flex-row-reverse' : 'flex-row'}`}>
            <span className="text-[10px] text-muted-foreground">{fmtTime(msg.sentAt)}</span>
            {isOut && <StatusIcon status={msg.readStatus} />}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex ${isOut ? 'justify-end' : 'justify-start'} mb-3`}>
      {!isOut && (
        <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0 mr-2 self-end">
          <AppImage src={ANDRE_PHOTO_URL} alt="André" width={32} height={32} className="w-full h-full object-cover object-top" />
        </div>
      )}
      <div className={`flex flex-col gap-0.5 max-w-[80%] ${isOut ? 'items-end' : 'items-start'}`}>
        {msg.type === 'photo' && (
          <PhotoBubble msg={msg} isOut={isOut} onOpen={onOpenPhoto} />
        )}
        {msg.type === 'voice' && (
          <VoicePlayer msg={msg} isOut={isOut} />
        )}
        {msg.type === 'text' && (
          <div className={`px-3 py-2.5 rounded-2xl shadow-sm text-sm leading-relaxed ${isOut ? 'bg-lucat-green text-white rounded-br-sm' : 'bg-white text-foreground rounded-bl-sm'}`}>
            {msg.content}
          </div>
        )}
        <div className={`flex items-center gap-1 px-1 ${isOut ? 'flex-row-reverse' : 'flex-row'}`}>
          <span className="text-[10px] text-muted-foreground">{fmtTime(msg.sentAt)}</span>
          {isOut && <StatusIcon status={msg.readStatus} />}
        </div>
      </div>
    </div>
  );
}

// ─── Photo lightbox ───────────────────────────────────────────────────────────

function PhotoLightbox({ url, onClose }: { url: string; onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/95 flex flex-col items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-label="Visionneuse photo"
    >
      <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-4 pt-safe-top pb-3 bg-gradient-to-b from-black/60 to-transparent">
        <button
          onClick={onClose}
          aria-label="Fermer la photo"
          className="w-11 h-11 flex items-center justify-center rounded-xl bg-white/10 text-white tap-target active:scale-95 transition-all"
        >
          <X size={22} />
        </button>
        <span className="text-white/80 text-sm font-medium">Photo</span>
        <div className="w-11" />
      </div>
      <div className="w-full flex-1 flex items-center justify-center px-4">
        <AppImage
          src={url}
          alt="Photo partagée avec André, vue en plein écran"
          width={400}
          height={400}
          className="max-w-full max-h-[70vh] object-contain rounded-xl"
        />
      </div>
      <div className="pb-safe-bottom pb-6">
        <button
          onClick={onClose}
          aria-label="Fermer"
          className="bg-white/10 text-white rounded-2xl px-8 py-3 text-sm font-semibold tap-target active:scale-95 transition-all"
        >
          Fermer
        </button>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

type ConvView = 'list' | 'conversation';

export default function MessagesClient() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { state, addMessage, addActivity, handleNotification, deferNotification } = useFamilyDemo();
  const [view, setView] = useState<ConvView>('list');
  const [activeContact] = useState<string>('andre');
  const [inputText, setInputText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [tick, setTick] = useState(0);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-open conversation if navigated with ?open=andre or from home
  useEffect(() => {
    const open = searchParams?.get('open');
    if (open === 'andre' || open === 'conversation') {
      setView('conversation');
    }
  }, [searchParams]);

  const getMessages = useCallback(() => {
    // Get messages for the active conversation from provider state
    const conv = state.conversations.find((c) => c.seniorId === 'andre' && c.familyUserId === 'marie');
    const msgIds = new Set(conv?.messageIds ?? []);
    return state.messages
      .filter((m) => m.conversationId === 'conv-andre-marie' || msgIds.has(m.id))
      .slice()
      .sort((a, b) => new Date(a.createdAt ?? a.sentAt ?? '').getTime() - new Date(b.createdAt ?? b.sentAt ?? '').getTime());
  }, [state.messages, state.conversations, activeContact, tick]);

  const messages = getMessages();

  const unreadAlerts = 0;
  const unreadMessages = 0;

  // Scroll to bottom when conversation opens or new message arrives
  useEffect(() => {
    if (view === 'conversation') {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [view, tick]);

  // ── Send text ──
  const handleSendText = () => {
    const text = inputText.trim();
    if (!text || isSending) return;
    setIsSending(true);
    setInputText('');
    const now = new Date().toISOString();
    setTimeout(() => {
      addMessage({
        id: `msg-text-${Date.now()}`,
        conversationId: 'conv-andre-marie',
        senderId: 'marie',
        recipientId: 'andre',
        type: 'text',
        text,
        createdAt: now,
        status: 'sent',
        // Legacy compat
        contactId: 'marie',
        seniorId: 'andre',
        content: text,
        sentAt: now,
        readStatus: 'sent',
        direction: 'outgoing',
      });
      addActivity({
        id: `act-msg-${Date.now()}`,
        seniorId: 'andre',
        actorId: 'marie',
        type: 'message',
        title: 'Vous avez envoyé un message à André',
        createdAt: now,
        status: 'informational',
        // Legacy compat
        category: 'message',
        action: 'text_sent',
        label: 'Vous avez envoyé un message à André',
        contactId: 'marie',
      });
      setIsSending(false);
      setTick((t) => t + 1);
    }, 600);
  };

  // ── Callback actions ──
  const handleCallbackCall = (notifId?: string) => {
    if (notifId) {
      handleNotification(notifId, 'read');
    }
    router.push(`/outgoing-call-screen${notifId ? `?notifId=${notifId}` : ''}`);
  };

  const handleCallbackLater = (notifId?: string) => {
    if (notifId) {
      deferNotification(notifId);
    }
    setTick((t) => t + 1);
  };

  const handleCallAgain = () => {
    router.push('/outgoing-call-screen');
  };

  const handleCall = () => {
    router.push('/outgoing-call-screen');
  };

  const handleVoice = () => {
    router.push('/send-screen?action=voice');
  };

  const handlePhoto = () => {
    router.push('/send-screen?action=photo');
  };

  // ── Last message preview ──
  const lastMsg = messages[messages.length - 1];
  const lastPreview = lastMsg
    ? lastMsg.type === 'photo' ? '📷 Photo'
    : lastMsg.type === 'voice' ? '🎤 Message vocal'
    : lastMsg.type === 'call' ? '📞 Appel'
    : lastMsg.type === 'notification' ? '🔔 ' + lastMsg.content
    : lastMsg.content.length > 40 ? lastMsg.content.slice(0, 40) + '…'
    : lastMsg.content
    : 'Aucun message';

  const lastTime = lastMsg ? fmtDateLabel(lastMsg.sentAt) : '';
  const unreadCount = messages.filter((m) => m.direction === 'incoming' && m.readStatus !== 'seen' && m.readStatus !== 'handled').length;

  // ── André status ──
  const senior = state.seniorProfile;
  const isOnline = senior.connectionStatus.toLowerCase().includes('connecté');

  // ─── Conversation view ────────────────────────────────────────────────────

  if (view === 'conversation') {
    return (
      <>
        {lightboxUrl && (
          <PhotoLightbox url={lightboxUrl} onClose={() => setLightboxUrl(null)} />
        )}
        <div className="flex flex-col h-screen bg-[#F5F0E8]">
          {/* Conversation header */}
          <div className="bg-lucat-navy px-4 pt-safe-top pb-3 flex items-center gap-3 flex-shrink-0 shadow-md">
            <button
              onClick={() => setView('list')}
              aria-label="Retour à la liste des messages"
              className="w-11 h-11 flex items-center justify-center rounded-xl bg-white/10 text-white tap-target active:scale-95 transition-all flex-shrink-0"
            >
              <ArrowLeft size={20} />
            </button>
            <div className="w-11 h-11 rounded-full overflow-hidden flex-shrink-0 border-2 border-lucat-green/50">
              <AppImage src={ANDRE_PHOTO_URL} alt="André Beaumont, portrait" width={44} height={44} className="w-full h-full object-cover object-top" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-bold text-base leading-tight">André</p>
              <p className="text-white/60 text-xs">Mon père</p>
              <div className="flex items-center gap-1 mt-0.5">
                <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${isOnline ? 'bg-lucat-green' : 'bg-white/30'}`} />
                <span className="text-white/50 text-[10px]">
                  {isOnline ? 'Connecté récemment' : 'Hors ligne'}
                </span>
              </div>
            </div>
            <button
              onClick={handleCall}
              aria-label="Appeler André"
              className="flex items-center gap-1.5 bg-lucat-green text-white rounded-xl px-3 py-2.5 text-xs font-bold tap-target active:scale-95 transition-all flex-shrink-0"
            >
              <Phone size={16} />
              <span className="hidden sm:inline">Appeler André</span>
            </button>
          </div>

          {/* Messages scroll area */}
          <div className="flex-1 overflow-y-auto px-4 py-3">
            {messages.length === 0 && (
              <div className="flex flex-col items-center justify-center h-full gap-3 text-center py-12">
                <div className="w-16 h-16 rounded-full bg-lucat-navy/10 flex items-center justify-center">
                  <PhoneCall size={28} className="text-lucat-navy/40" />
                </div>
                <p className="text-muted-foreground text-sm">Aucun message pour le moment.<br />Envoyez le premier !</p>
              </div>
            )}
            {messages.map((msg, i) => {
              const prev = messages[i - 1];
              const showSep = !prev || fmtDateLabel(msg.sentAt) !== fmtDateLabel(prev.sentAt);
              return (
                <React.Fragment key={msg.id}>
                  {showSep && (
                    <div className="flex justify-center my-4">
                      <span className="bg-white/80 text-muted-foreground text-[11px] font-semibold px-4 py-1.5 rounded-full shadow-sm">
                        {fmtDateLabel(msg.sentAt)}
                      </span>
                    </div>
                  )}
                  <MessageBubble
                    msg={msg}
                    onOpenPhoto={(url) => setLightboxUrl(url)}
                    onCallAgain={handleCallAgain}
                    onCallbackCall={handleCallbackCall}
                    onCallbackLater={handleCallbackLater}
                  />
                </React.Fragment>
              );
            })}
            {isSending && (
              <div className="flex justify-end mb-3">
                <div className="bg-lucat-green/60 text-white px-4 py-2.5 rounded-2xl rounded-br-sm text-sm flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 bg-white rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-1.5 h-1.5 bg-white rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-1.5 h-1.5 bg-white rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Fixed send bar */}
          <div className="bg-white border-t border-border px-3 py-2.5 flex items-end gap-2 flex-shrink-0 safe-bottom">
            <button
              onClick={handlePhoto}
              aria-label="Envoyer une photo"
              className="w-11 h-11 flex items-center justify-center rounded-xl bg-lucat-teal-light text-lucat-teal tap-target active:scale-95 transition-all flex-shrink-0"
            >
              <Camera size={20} />
            </button>
            <button
              onClick={handleVoice}
              aria-label="Enregistrer un message vocal"
              className="w-11 h-11 flex items-center justify-center rounded-xl bg-lucat-purple-light text-lucat-purple tap-target active:scale-95 transition-all flex-shrink-0"
            >
              <Mic size={20} />
            </button>
            <div className="flex-1 bg-[#F5F0E8] rounded-2xl px-3 py-2 min-h-[44px] flex items-center">
              <textarea
                ref={textareaRef}
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendText();
                  }
                }}
                placeholder="Écrire un message à André…"
                rows={1}
                aria-label="Écrire un message à André"
                className="w-full bg-transparent text-sm text-foreground placeholder:text-muted-foreground resize-none outline-none leading-relaxed"
                style={{ maxHeight: '80px' }}
              />
            </div>
            <button
              onClick={handleSendText}
              disabled={!inputText.trim() || isSending}
              aria-label="Envoyer le message"
              className={`w-11 h-11 flex items-center justify-center rounded-xl tap-target active:scale-95 transition-all flex-shrink-0 ${inputText.trim() && !isSending ? 'bg-lucat-green text-white shadow-sm' : 'bg-muted text-muted-foreground cursor-not-allowed'}`}
            >
              <Send size={18} />
            </button>
          </div>
        </div>
      </>
    );
  }

  // ─── Conversation list ────────────────────────────────────────────────────

  return (
    <AppLayout unreadAlerts={unreadAlerts} unreadMessages={unreadMessages}>
      <div className="pb-4">
        {/* Header */}
        <div className="bg-lucat-navy px-4 py-4">
          <h1 className="text-white text-2xl font-bold">Messages</h1>
          <p className="text-white/60 text-sm mt-0.5">Vos conversations avec André</p>
        </div>

        {/* André conversation card */}
        <div className="px-4 pt-4 flex flex-col gap-2">
          <button
            onClick={() => setView('conversation')}
            aria-label="Ouvrir la conversation avec André"
            className="w-full bg-card rounded-2xl p-4 shadow-card border border-border flex items-center gap-3 tap-target active:scale-[0.98] transition-all duration-150 text-left"
          >
            <div className="relative flex-shrink-0">
              <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-lucat-green/30">
                <AppImage src={ANDRE_PHOTO_URL} alt="André Beaumont" width={56} height={56} className="w-full h-full object-cover object-top" />
              </div>
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[20px] h-5 bg-lucat-red text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
                  {unreadCount}
                </span>
              )}
              <span className={`absolute bottom-0 right-0 w-3.5 h-3.5 rounded-full border-2 border-card ${isOnline ? 'bg-lucat-green' : 'bg-muted-foreground'}`} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <p className="text-lucat-navy font-bold text-base">André</p>
                <span className="text-muted-foreground text-xs flex-shrink-0">{lastTime}</span>
              </div>
              <p className="text-muted-foreground text-xs mb-1">Mon père</p>
              <div className="flex items-center gap-1.5">
                {lastMsg?.type === 'photo' && <ImageIcon size={12} className="text-lucat-teal flex-shrink-0" />}
                {lastMsg?.type === 'voice' && <Mic size={12} className="text-lucat-purple flex-shrink-0" />}
                {lastMsg?.type === 'call' && <Phone size={12} className="text-lucat-green flex-shrink-0" />}
                <p className={`text-sm truncate ${unreadCount > 0 ? 'text-foreground font-semibold' : 'text-muted-foreground'}`}>
                  {lastPreview}
                </p>
              </div>
            </div>
          </button>

          {/* Quick action buttons */}
          <div className="grid grid-cols-3 gap-2 mt-1">
            <button
              onClick={handleCall}
              aria-label="Appeler André"
              className="flex flex-col items-center gap-1.5 bg-lucat-green text-white rounded-2xl py-3 px-2 tap-target active:scale-95 transition-all shadow-sm"
            >
              <Phone size={20} />
              <span className="text-xs font-bold text-center leading-tight">Appeler</span>
            </button>
            <button
              onClick={handlePhoto}
              aria-label="Envoyer une photo à André"
              className="flex flex-col items-center gap-1.5 bg-lucat-teal-light text-lucat-teal rounded-2xl py-3 px-2 tap-target active:scale-95 transition-all"
            >
              <Camera size={20} />
              <span className="text-xs font-bold text-center leading-tight">Photo</span>
            </button>
            <button
              onClick={handleVoice}
              aria-label="Enregistrer un message vocal pour André"
              className="flex flex-col items-center gap-1.5 bg-lucat-purple-light text-lucat-purple rounded-2xl py-3 px-2 tap-target active:scale-95 transition-all"
            >
              <Mic size={20} />
              <span className="text-xs font-bold text-center leading-tight">Vocal</span>
            </button>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
